/**
 * Accept / reject / cancel action handlers + their local state for
 * {@link ChangeRequestsTab}. Extracted into a hook so the tab component
 * stays under the 500-line file limit; behaviour is unchanged.
 *
 * @param line - the PO line whose proposed changes are being actioned
 * @param activeChangeFields - the fields with an open proposal (date/price/quantity)
 * @param onUpdate - refetch callback fired after a successful action
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { PurchaseOrderLine, purchasingAPI } from '../../lib/api';
import type { ChangeField } from './changeRequestsTabHelpers';

export function useChangeRequestActions(
  line: PurchaseOrderLine,
  activeChangeFields: ChangeField[],
  onUpdate?: () => void,
) {
  const [fieldActionLoading, setFieldActionLoading] = useState<string | null>(null);
  const [rejectField, setRejectField] = useState<ChangeField | 'all' | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleAcceptField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = line.id;
      if (field === 'date') {
        await purchasingAPI.acceptDeliveryDate(lineId, {});
      } else if (field === 'quantity') {
        await purchasingAPI.acceptQuantityChange(lineId, {});
      } else if (field === 'price') {
        await purchasingAPI.acceptPriceChange(lineId, {});
      }
      toast.success(`${field === 'date' ? 'Promise date' : field === 'price' ? 'Unit price' : 'Quantity'} change accepted`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to accept change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleRejectField = async () => {
    if (!rejectField || !rejectReason.trim()) return;
    setFieldActionLoading(rejectField);
    const lineId = line.id;
    const targets: ChangeField[] = rejectField === 'all' ? activeChangeFields : [rejectField];
    try {
      // CR-4706 round 2: sequential, same rationale as handleAcceptAll —
      // parallel rejects race on the same PO line row and lose clears.
      let failed = 0;
      for (const f of targets) {
        try {
          await purchasingAPI.rejectFieldChange(lineId, { field: f, reason: rejectReason });
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) toast.success(targets.length > 1 ? 'All changes rejected' : `${targets[0] === 'date' ? 'Promise date' : targets[0] === 'price' ? 'Unit price' : 'Quantity'} change rejected`);
      else toast.error(`${failed} change${failed > 1 ? 's' : ''} failed to reject`);
      setRejectField(null);
      setRejectReason('');
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to reject change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleAcceptAll = async () => {
    setFieldActionLoading('all');
    const lineId = line.id;
    try {
      // CR-4706 round 2: process sequentially, NOT in parallel. Each accept
      // endpoint loads the PO line, clears one proposed_* field, and saves.
      // When these ran via Promise.allSettled the three calls raced on the
      // same row — each worker read a stale snapshot with all three
      // proposed_* fields still populated, cleared its own, and the last
      // writer clobbered the earlier clears. The UI then refreshed and
      // showed Quantity + Unit Price still "Active" even though all three
      // audit rows had been written. Sequential awaits avoid the race and
      // keep the per-field action separable (so a failure on one doesn't
      // abort the rest — we surface a partial-failure toast).
      let failed = 0;
      for (const f of activeChangeFields) {
        try {
          if (f === 'date') await purchasingAPI.acceptDeliveryDate(lineId, {});
          else if (f === 'price') await purchasingAPI.acceptPriceChange(lineId, {});
          else await purchasingAPI.acceptQuantityChange(lineId, {});
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) toast.success('All changes accepted');
      else toast.error(`${failed} change${failed > 1 ? 's' : ''} failed to accept`);
      onUpdate?.();
    } finally {
      setFieldActionLoading(null);
    }
  };

  // CR-4680: Supplier cancels their own proposed change
  const handleCancelField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = line.id;
      await purchasingAPI.cancelFieldChange(lineId, { field });
      toast.success(`${field === 'date' ? 'Promise date' : field === 'price' ? 'Unit price' : 'Quantity'} change cancelled`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to cancel change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  return {
    fieldActionLoading,
    rejectField,
    setRejectField,
    rejectReason,
    setRejectReason,
    handleAcceptField,
    handleRejectField,
    handleAcceptAll,
    handleCancelField,
  };
}
